import React from 'react';
import { Typography, Box } from '@mui/material';

/**
 * ArtisticScoringRubric
 *
 * Drop this component into any page wrapped by your MUI <ThemeProvider>.
 * It pulls colors from your CSS variables (--color-primary, --color-text, etc.)
 * and typography from the MUI theme, so it automatically respects light/dark mode.
 *
 * Usage:  <ArtisticScoringRubric />
 */

const criteria = [
  {
    name: 'Use of Space',
    description: 'Levels, pathways, formations, and floor patterns',
    levels: [
      'Choreography stays in one area or level; little use of pathways or formations',
      'Some variation in levels or formations but the performance space is underused',
      'Good use of the full space with clear pathways and varied formations',
      'Inventive, purposeful use of all spatial dimensions; formations enhance meaning',
    ],
  },
  {
    name: 'Use of Time',
    description: 'Rhythm, tempo, accent, duration, and musicality',
    levels: [
      'Movement shows little connection to the music; timing feels arbitrary',
      'Movement follows the beat but rarely explores tempo changes or accents',
      'Clear musicality with intentional use of tempo shifts and rhythmic accents',
      'Sophisticated interplay with the music; tempo, accent, and silence are used as choreographic tools',
    ],
  },
  {
    name: 'Use of Energy',
    description: 'Force, flow, and movement quality',
    levels: [
      'Movement quality is flat; little variation in force or flow throughout',
      'Some contrast in energy but shifts feel unintentional or underdeveloped',
      'Clear, deliberate variation between light, strong, sustained, and sudden qualities',
      'Rich, nuanced energy choices that heighten mood and meaning throughout the piece',
    ],
  },
  {
    name: 'Use of Body',
    description: 'Body actions, shapes, and parts; locomotor and non-locomotor movement',
    levels: [
      'Limited range of body actions; relies on repetitive or familiar movements',
      'Some variety in body shapes and actions but exploration is surface-level',
      'Good variety of body parts, shapes, and actions used with clear intent',
      'Full, expressive use of the body as an instrument; movement vocabulary is diverse and purposeful',
    ],
  },
  {
    name: 'Relationship',
    description: 'Dancer-to-dancer connections; groupings and partnering',
    levels: [
      'Little evidence of choreographed interaction between dancers',
      'Some partnering or grouping is present but feels incidental rather than designed',
      'Groupings, partnering, and spatial relationships are clearly choreographed',
      'Relationships between dancers are integral to the work; groupings deepen the artistic intent',
    ],
  },
  {
    name: 'Structure & Form',
    description: 'Choreographic form, transitions, motif development',
    levels: [
      'No clear structure; sections feel disconnected with abrupt or missing transitions',
      'A basic structure is apparent but transitions are rough and motifs are undeveloped',
      'Clear beginning, development, and ending; smooth transitions with recognizable motifs',
      'Sophisticated compositional structure; motifs are developed and transformed; seamless flow',
    ],
  },
  {
    name: 'Creativity',
    description: 'Originality of concept and movement; evidence of student creative process',
    levels: [
      'Movement is largely borrowed or imitative; little personal voice evident',
      'Some original ideas present but much of the choreography feels derivative',
      'A clear personal or group voice; creative choices are evident and intentional',
      'Highly original concept and vocabulary; the work is imaginative and distinctly the students\' own',
    ],
  },
];

const levelHeaders = [
  { num: '1', label: 'Limited' },
  { num: '2', label: 'Moderate' },
  { num: '3', label: 'Considerable' },
  { num: '4', label: 'Excellent' },
];

const levelColors = ['#d94f4f', '#d4903a', '#3a8fb7', '#2e7d5e'];

const styles = {
  container: {
    width: '100%',
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '24px',
    backgroundColor: 'var(--color-background)',
    color: 'var(--color-text)',
  },
  header: {
    textAlign: 'center',
    paddingBottom: '12px',
    borderBottom: '3px solid var(--color-primary)',
    marginBottom: '16px',
  },
  elementsLegend: {
    textAlign: 'center',
    marginBottom: '12px',
    fontSize: '12px',
    color: 'var(--color-text)',
    opacity: 0.7,
  },
  elementHighlight: {
    fontWeight: 600,
    color: 'var(--color-primary)',
    margin: '0 4px',
  },
  scoreKey: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  keyItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  dot: (color) => ({
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: '11px',
    backgroundColor: color,
  }),
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '1rem',
  },
  th: {
    backgroundColor: 'var(--color-bg-header)',
    color: 'var(--color-background)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontSize: '0.8rem',
    padding: '10px 8px',
    textAlign: 'center',
    border: '1px solid var(--color-bg-header)',
  },
  thFirst: {
    textAlign: 'left',
    width: '18%',
  },
  thLevel: {
    width: '20.5%',
  },
  td: {
    padding: '8px',
    verticalAlign: 'top',
    borderRight: '1px solid',
    borderBottom: '1px solid',
    borderColor: 'rgba(128,128,128,0.25)',
    lineHeight: 1.35,
  },
  tdFirst: {
    borderLeft: '1px solid',
    borderColor: 'rgba(128,128,128,0.25)',
  },
  criteriaName: {
    fontWeight: 700,
    marginBottom: '2px',
    color: 'var(--color-primary)',
  },
  criteriaDesc: {
    fontSize: '0.8rem',
    opacity: 0.6,
    fontStyle: 'italic',
    lineHeight: 1.25,
  },
  levelCell: {},
};

const ArtisticScoringRubric = () => {
  return (
    <Box sx={styles.container}>
      {/* Header */}
      <Box sx={styles.header}>
        <Typography variant="title" component="h1" sx={{ color: 'var(--color-primary)' }}>
          Artistic Scoring Rubric
        </Typography>
        <Typography
          variant="h3"
          component="p"
          sx={{ fontSize: '13px', letterSpacing: '3px', opacity: 0.6, textTransform: 'uppercase', fontStyle: 'normal' }}
        >
          Ontario Secondary School Dancefest
        </Typography>
      </Box>

      {/* Score Key */}
      <Box sx={styles.scoreKey}>
        {levelHeaders.map((lh, i) => (
          <Box key={lh.num} sx={styles.keyItem}>
            <span style={styles.dot(levelColors[i])}>{lh.num}</span>
            {lh.label}
          </Box>
        ))}
      </Box>

      {/* Rubric Table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, ...styles.thFirst }}>Criterion</th>
            {levelHeaders.map((lh) => (
              <th key={lh.num} style={{ ...styles.th, ...styles.thLevel }}>
                {lh.num} — {lh.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {criteria.map((c, rowIndex) => (
            <tr
              key={c.name}
              style={rowIndex % 2 === 1 ? { backgroundColor: 'rgba(128,128,128,0.05)' } : {}}
            >
              <td style={{ ...styles.td, ...styles.tdFirst }}>
                <Typography variant="h2" component="div" sx={{ ...styles.criteriaName, fontSize: '1.1rem' }}>
                  {c.name}
                </Typography>
                {c.element && <span style={styles.elementTag}>{c.element}</span>}
                <div style={styles.criteriaDesc}>{c.description}</div>
              </td>
              {c.levels.map((level, li) => (
                <td key={li} style={{ ...styles.td, ...styles.levelCell }}>
                  {level}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

export default ArtisticScoringRubric;
